import Lottie from 'react-lottie';
import { clsx } from 'clsx';

import Typography from '~/shared/components/Typography';
import GrowActionIcon from '~/shared/assets/icons/grow-action.svg';
import ShrinkActionIcon from '~/shared/assets/icons/shrink-action.svg';
import ModifyIcon from '~/shared/assets/icons/modify.svg';
import ActivateAutoscalingIcon from '~/shared/assets/icons/activate-autoscaling.svg';
import DeactivateAutoscalingIcon from '~/shared/assets/icons/deactivate-autoscaling.svg';
import loadingSpinner from '~/shared/assets/lotties/loading-spinner.json';
import Caution from '~/shared/assets/icons/caution.svg';
import { OptLogVolumeActionsState, OptLogVolumeActionsTypes } from '~/shared/types/api';

import ErrorTooltip from './ErrorTooltip';

interface TypeCellProps {
  type: OptLogVolumeActionsTypes;
  volumeId?: string;
  progress?: number;
  state?: OptLogVolumeActionsState;
  errorMessage?: string;
}

const getDisplayProperties = (type: OptLogVolumeActionsTypes) => {
  switch (type) {
    case 'Growth':
      return {
        IconComponent: GrowActionIcon,
        text: 'Grow',
        typographyColor: 'success' as const,
      };
    case 'Shrink':
      return {
        IconComponent: ShrinkActionIcon,
        text: 'Shrink',
        typographyColor: 'error' as const,
      };
    case 'Modify':
      return {
        IconComponent: ModifyIcon,
        text: 'Modify',
        typographyColor: undefined,
      };
    case 'Activate Autoscaling':
      return {
        IconComponent: ActivateAutoscalingIcon,
        text: 'Activate Autoscaling',
        typographyColor: undefined,
      };
    case 'Deactivate Autoscaling':
      return {
        IconComponent: DeactivateAutoscalingIcon,
        text: 'Deactivate Autoscaling',
        typographyColor: undefined,
      };
    default:
      // Fallback for unknown types
      return {
        IconComponent: undefined,
        text: type,
        typographyColor: undefined,
      };
  }
};

export default function TypeCell({ type, volumeId, progress, state, errorMessage }: TypeCellProps) {
  const { IconComponent, text, typographyColor } = getDisplayProperties(type);

  const baseTooltipId = volumeId
    ? `type-cell-tooltip-${volumeId}`
    : `type-cell-tooltip-${type.replace(/\s+/g, '-').toLowerCase()}`;
  const errorTooltipId = `${baseTooltipId}-error`;

  return (
    <>
      <div
        className="flex items-center gap-[6px] justify-between w-full "
        // Only attach the generic tooltip when not in error/canceled state to avoid duplicates
        data-tooltip-id={
          state && (state === 'Failure' || state === 'Canceled') ? undefined : baseTooltipId
        }
        data-tooltip-content={
          state && (state === 'Failure' || state === 'Canceled') ? undefined : text
        }
      >
        <div className="flex items-center gap-[8px]">
          {IconComponent && <IconComponent className="size-9" />}
          <Typography variant="p3" color={typographyColor} className="text-primary-900">
            {text}
          </Typography>
        </div>
        {/* Wrapper for the status icon to help stabilize its position */}
        <div className="w-[28.24px] h-[28.24px] flex items-center justify-center ">
          {(
            [
              'Shrink',
              'Modify',
              'Activate Autoscaling',
              'Deactivate Autoscaling',
            ] as OptLogVolumeActionsTypes[]
          ).includes(type) &&
            (state === 'Start' || state === 'Progress') && (
              <div className="relative flex items-center justify-center">
                <span className="-ml-2 -mr-4 inline-block">
                  <Lottie
                    options={{
                      loop: true,
                      autoplay: true,
                      animationData: loadingSpinner,
                      rendererSettings: {
                        preserveAspectRatio: 'xMidYMid meet',
                      },
                    }}
                    height={28}
                    width={56}
                    isClickToPauseDisabled
                  />
                </span>
                {typeof progress === 'number' && progress >= 0 && (
                  <span className="absolut text-[10px] font-medium text-primary-900">
                    {progress === 0 && (type === 'Activate Autoscaling' || type === 'Deactivate Autoscaling') 
                      ? 'Queued' 
                      : `${Math.round(progress)}%`}
                  </span>
                )}
                {typeof progress !== 'number' && (type === 'Activate Autoscaling' || type === 'Deactivate Autoscaling') && (
                  <span className="absolut text-[10px] font-medium text-primary-900">
                    Queued
                  </span>
                )}
              </div>
            )}
          {(state === 'Failure' || state === 'Canceled') && (
            <Caution
              className={clsx(
                'block size-6',
                state === 'Canceled' && 'text-gray-600',
                state === 'Failure' && 'text-red'
              )}
              data-tooltip-id={errorTooltipId}
            />
          )}
        </div>
      </div>
      {(state === 'Failure' || state === 'Canceled') && (
        <ErrorTooltip
          tooltipId={errorTooltipId}
          message={errorMessage || (state === 'Failure' ? 'Failed' : 'Canceled')}
        />
      )}
    </>
  );
}
